import React, { useState, useEffect } from 'react';
import { User, Settings, Save, RefreshCw, Mail, Phone, MapPin } from 'lucide-react';
import SuperAdminSidebar from '../../components/SuperAdminsidebar';
import { fetchProfile, updateProfile, fetchAppSettings, updateAppSettings } from '../../../services/superadmin_services/settings_services';

const SettingsPage = () => {
  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    address: ''
  });
  
  const [appSettings, setAppSettings] = useState({
    contact_email: '',
    contact_phone: '',
    contact_address: ''
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({ profile: false, app: false });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState({ profile: '', app: '' });

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Load profile and app settings in parallel
      const [profileResponse, appSettingsResponse] = await Promise.all([
        fetchProfile(),
        fetchAppSettings()
      ]);
      
      setProfile({
        first_name: profileResponse.data.profile.first_name || '',
        last_name: profileResponse.data.profile.last_name || '',
        phone: profileResponse.data.profile.phone || '',
        address: profileResponse.data.profile.address || ''
      });
      
      setAppSettings({
        contact_email: appSettingsResponse.data.app_settings.contact_email || '',
        contact_phone: appSettingsResponse.data.app_settings.contact_phone || '',
        contact_address: appSettingsResponse.data.app_settings.contact_address || ''
      });
    } catch (error) {
      setError(error.message);
      console.error('Error loading settings data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle profile form changes
  const handleProfileChange = (field, value) => {
    setProfile(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear success message when user starts typing
    if (success.profile) {
      setSuccess(prev => ({ ...prev, profile: '' }));
    }
  };

  // Handle app settings form changes
  const handleAppSettingsChange = (field, value) => {
    setAppSettings(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear success message when user starts typing
    if (success.app) {
      setSuccess(prev => ({ ...prev, app: '' }));
    }
  };

  // Handle profile form submission
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(prev => ({ ...prev, profile: true }));
      setError('');
      setSuccess(prev => ({ ...prev, profile: '' }));
      
      const response = await updateProfile(profile);
      
      setSuccess(prev => ({ ...prev, profile: 'Profile updated successfully!' }));
      
      // Update profile with response data
      setProfile({
        first_name: response.data.profile.first_name || '',
        last_name: response.data.profile.last_name || '',
        phone: response.data.profile.phone || '',
        address: response.data.profile.address || ''
      });
    } catch (error) {
      setError(error.message);
    } finally {
      setSaving(prev => ({ ...prev, profile: false }));
    }
  };

  // Handle app settings form submission
  const handleAppSettingsSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(prev => ({ ...prev, app: true }));
      setError('');
      setSuccess(prev => ({ ...prev, app: '' }));
      
      const response = await updateAppSettings(appSettings);
      
      setSuccess(prev => ({ ...prev, app: 'App settings updated successfully!' }));
      
      // Update app settings with response data
      setAppSettings({
        contact_email: response.data.app_settings.contact_email || '',
        contact_phone: response.data.app_settings.contact_phone || '',
        contact_address: response.data.app_settings.contact_address || ''
      });
    } catch (error) {
      setError(error.message);
    } finally {
      setSaving(prev => ({ ...prev, app: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <SuperAdminSidebar />
        
        <div className="flex-1 ml-64 p-8">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
              <p className="text-gray-600 mt-2">Loading settings...</p>
            </div>

            {/* Loading skeleton */}
            <div className="space-y-8">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
                <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i}>
                      <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                      <div className="h-10 bg-gray-200 rounded"></div>
                    </div>
                  ))}
                </div>
                <div className="h-10 bg-gray-200 rounded w-32 mt-6"></div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
                <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[1, 2, 3].map((i) => (
                    <div key={i}>
                      <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                      <div className="h-10 bg-gray-200 rounded"></div>
                    </div>
                  ))}
                </div>
                <div className="h-10 bg-gray-200 rounded w-32 mt-6"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      <SuperAdminSidebar />
      
      <div className="flex-1 ml-64 p-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
                <p className="text-gray-600 mt-2">Manage your profile and application settings</p>
              </div>
              <button
                onClick={loadData}
                disabled={loading}
                className="px-4 py-2 bg-tertiary-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6">
              <p className="font-medium">Error</p>
              <p className="text-sm mt-1">{error}</p>
            </div>
          )}

          <div className="space-y-8">
            {/* Profile Settings */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-secondary-100 rounded-lg flex items-center justify-center">
                  <User className="w-5 h-5 text-secondary-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Profile Settings</h2>
                  <p className="text-gray-600 text-sm">Update your personal information</p>
                </div>
              </div>

              {/* Success Message for Profile */}
              {success.profile && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md mb-6">
                  {success.profile}
                </div>
              )}

              <form onSubmit={handleProfileSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={profile.first_name}
                      onChange={(e) => handleProfileChange('first_name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500"
                      placeholder="Enter your first name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={profile.last_name}
                      onChange={(e) => handleProfileChange('last_name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500"
                      placeholder="Enter your last name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={profile.phone}
                      onChange={(e) => handleProfileChange('phone', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500"
                      placeholder="Enter your phone number"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Address
                    </label>
                    <input
                      type="text"
                      value={profile.address}
                      onChange={(e) => handleProfileChange('address', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500"
                      placeholder="Enter your address"
                    />
                  </div>
                </div>

                <div className="mt-6">
                  <button
                    type="submit"
                    disabled={saving.profile}
                    className="px-6 py-2 bg-secondary-600 text-white rounded-lg hover:bg-secondary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {saving.profile ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        <span>Save Profile</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* App Settings */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-tertiary-100 rounded-lg flex items-center justify-center">
                  <Settings className="w-5 h-5 text-tertiary-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">App Settings</h2>
                  <p className="text-gray-600 text-sm">Update application contact information</p>
                </div>
              </div>

              {/* Success Message for App Settings */}
              {success.app && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md mb-6">
                  {success.app}
                </div>
              )}

              <form onSubmit={handleAppSettingsSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Mail className="w-4 h-4 inline mr-1" />
                      Contact Email
                    </label>
                    <input
                      type="email"
                      value={appSettings.contact_email}
                      onChange={(e) => handleAppSettingsChange('contact_email', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-tertiary-500"
                      placeholder="Enter contact email"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Phone className="w-4 h-4 inline mr-1" />
                      Contact Phone
                    </label>
                    <input
                      type="tel"
                      value={appSettings.contact_phone}
                      onChange={(e) => handleAppSettingsChange('contact_phone', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-tertiary-500"
                      placeholder="Enter contact phone"
                      required
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      Contact Address
                    </label>
                    <textarea
                      value={appSettings.contact_address}
                      onChange={(e) => handleAppSettingsChange('contact_address', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-tertiary-500"
                      placeholder="Enter contact address"
                      required
                    />
                  </div>
                </div>

                <div className="mt-6">
                  <button
                    type="submit"
                    disabled={saving.app}
                    className="px-6 py-2 bg-tertiary-600 text-white rounded-lg hover:bg-tertiary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {saving.app ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        <span>Save App Settings</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;